import dayjs from 'dayjs'
import { createSessionMarker } from './sessionMarker'

export interface BuildReloadOrigin {
    path: string
    version: string
}

export type BootBuildCheckResult = 'fresh' | 'unreadable' | 'reload_skipped'

// Set just before a reload that moves a tab off an outdated build, consumed by the page load that
// follows so APM can label it.
const bootMarker = createSessionMarker('core:stale-boot-reload', 10_000)
const navigationMarker = createSessionMarker('core:outdated-build-reload', 10_000)
const idleMarker = createSessionMarker('core:idle-build-reload', 10_000)
const depositReturnMarker = createSessionMarker('core:deposit-return-full-load', 10_000)

const ORIGIN_KEY = 'core:build-reload:origin'
const COUNT_KEY = 'core:build-reload:count'
const ORIGIN_TTL_MS = 10_000

// Where and from which build the tab reloads, and how many build reloads this tab has made, so the page load
// that follows can tell a normal move from a loop or a reload in a risky place.
const recordOrigin = (origin: BuildReloadOrigin): void => {
    try {
        sessionStorage.setItem(COUNT_KEY, String(Number(sessionStorage.getItem(COUNT_KEY)) + 1))
        sessionStorage.setItem(ORIGIN_KEY, JSON.stringify({ ...origin, at: dayjs().valueOf() }))
    }
    catch { /* only the labels are lost */ }
}

export const markStaleBootReload = (origin: BuildReloadOrigin): void => {
    bootMarker.mark()
    recordOrigin(origin)
}
export const markOutdatedBuildReload = (origin: BuildReloadOrigin): void => {
    navigationMarker.mark()
    recordOrigin(origin)
}
export const markIdleBuildReload = (origin: BuildReloadOrigin): void => {
    idleMarker.mark()
    recordOrigin(origin)
}
export const markDepositReturnFullLoad = depositReturnMarker.mark

export const consumeStaleBootReloadMarker = bootMarker.consume
export const consumeOutdatedBuildReloadMarker = navigationMarker.consume
export const consumeIdleBuildReloadMarker = idleMarker.consume
export const consumeDepositReturnFullLoadMarker = depositReturnMarker.consume

/** APM labels for the page load right after a build reload; `null` for any other page load. */
export const consumeBuildReloadOrigin = (): Record<string, string | number> | null => {
    try {
        const raw = sessionStorage.getItem(ORIGIN_KEY)
        if (!raw) return null
        sessionStorage.removeItem(ORIGIN_KEY)

        const origin = JSON.parse(raw) as BuildReloadOrigin & { at: number }
        if (dayjs().valueOf() - origin.at > ORIGIN_TTL_MS) return null

        return {
            build_reload_from_path: origin.path,
            build_reload_from_version: origin.version,
            build_reloads_in_tab: Number(sessionStorage.getItem(COUNT_KEY)),
        }
    }
    catch {
        return null
    }
}

// The start check runs before the APM agent; it leaves its outcome here for the page load's labels.
let bootCheck: { ms: number, result: BootBuildCheckResult } | null = null

export const recordBootBuildCheck = (ms: number, result: BootBuildCheckResult): void => {
    bootCheck = { ms, result }
}

export const takeBootBuildCheck = (): { ms: number, result: BootBuildCheckResult } | null => {
    const check = bootCheck
    bootCheck = null
    return check
}

// Set by the server's stale-client purge (`server/plugins/staleClientPurge`). Cookies survive the
// cache clear, so a page load shortly after it is the one the purge produced.
const STALE_PURGE_COOKIE = 'core_stale_purge'
const PURGE_LABEL_WINDOW_MS = 120_000

export const followsStalePurge = (cookie: string, now = dayjs().valueOf()): boolean => {
    const value = cookie.split(';').map(part => part.trim()).find(part => part.startsWith(`${STALE_PURGE_COOKIE}=`))
    const purgedAt = Number(value?.slice(STALE_PURGE_COOKIE.length + 1))

    return purgedAt > 0 && now - purgedAt <= PURGE_LABEL_WINDOW_MS
}
